import { OfficeAccountingTableHeader } from "./officeAccountingConsts";
import { parseOfficeAccountingAmount } from "@/src/utils/officeAccountingTotalAmountUsd";

export type OfficeAccountingColumnConfig = {
  title: string;
  id: string;
  width?: number;
};

/** Legacy uniform width used before header-based defaults. */
const defaultWidth = 5;

/**
 * Estimate rem width from header title length (padding + optional edit icon).
 * Caps keep very long titles from dominating the table.
 */
const estimateOAPColumnWidthFromTitle = (title: string): number => {
  const estimated = title.length * 0.52 + 2.25;
  return Math.round(Math.max(defaultWidth, Math.min(estimated, 16)) * 100) / 100;
};

/** Floors for columns that usually need more space than the header alone. */
const OAP_CONTENT_MIN_WIDTH: Partial<Record<string, number>> = {
  fullName: 11.5,
  email: 9.69,
  managersEmail: 10,
  agentsEmail: 9.5,
  course: 9.69,
  fieldOfStudy: 10,
  comments: 8,
  phone: 7.5,
  dateOfBirth: 7.5,
  studentStatus: 7.5,
};

/** Exact default widths (override title-based estimate). */
const OAP_FIXED_WIDTH: Partial<Record<string, number>> = {
  applicantId: 5.5525,
};

const resolveOAPColumnWidth = (id: string, title: string): number => {
  const fixed = OAP_FIXED_WIDTH[id];
  if (fixed != null) return fixed;
  const fromTitle = estimateOAPColumnWidthFromTitle(title);
  const contentMin = OAP_CONTENT_MIN_WIDTH[id] ?? 0;
  return Math.max(fromTitle, contentMin);
};

export const officeAccountingAllFieldsList: OfficeAccountingColumnConfig[] = OfficeAccountingTableHeader.map((column) => ({
  title: column.title,
  id: column.id,
  width: resolveOAPColumnWidth(column.id, column.title),
}));

/** Default visible columns through Current Semester + Status.
 * Office Accounting ID and PG/PhD cost 1–6 are optional (settings only; not in this list).
 * Any columns saved in user OAPConfig are preserved after reload. */
const defaultVisibleColumnIds = [
  "applicantId",
  "fullName",
  "comments",
  "campus",
  "country",
  "dateOfBirth",
  "studentStatus",
  "passportNo",
  "passportIssued",
  "validPassport",
  "phone",
  "email",
  "managersEmail",
  "agentsEmail",
  "levelEducation",
  "course",
  "fieldOfStudy",
  "formOfEducation",
  "duration",
  "mediumOfInstruction",
  "admissionSemester",
  "intake",
  "currentSemester",
  "generalStatus",
];

export const officeAccountingDefaultConfiguration: OfficeAccountingColumnConfig[] = defaultVisibleColumnIds
  .map((id) => officeAccountingAllFieldsList.find((col) => col.id === id))
  .filter((col): col is OfficeAccountingColumnConfig => col != null);

const findOAPColumn = (id: string): OfficeAccountingColumnConfig | undefined =>
  officeAccountingAllFieldsList.find((col) => col.id === id);

const resolveSavedOAPColumnWidth = (
  savedWidth: number | undefined,
  canonicalWidth: number | undefined,
): number => {
  const canonical = canonicalWidth ?? defaultWidth;
  // Upgrade old uniform `5` defaults to header-based widths; keep user-resized values.
  if (savedWidth == null || savedWidth === defaultWidth) return canonical;
  return savedWidth;
};

const insertOAPColumnAfter = (
  columns: OfficeAccountingColumnConfig[],
  column: OfficeAccountingColumnConfig,
  afterId: string,
): OfficeAccountingColumnConfig[] => {
  const index = columns.findIndex((col) => col.id === afterId);
  if (index === -1) return [column, ...columns];
  return [...columns.slice(0, index + 1), column, ...columns.slice(index + 1)];
};

const dedupeOAPColumns = (columns: OfficeAccountingColumnConfig[]): OfficeAccountingColumnConfig[] => {
  const seen = new Set<string>();
  return columns.filter((col) => {
    if (seen.has(col.id)) return false;
    seen.add(col.id);
    return true;
  });
};

const insertOAPColumnBefore = (
  columns: OfficeAccountingColumnConfig[],
  column: OfficeAccountingColumnConfig,
  beforeId: string,
): OfficeAccountingColumnConfig[] => {
  const index = columns.findIndex((col) => col.id === beforeId);
  if (index === -1) return [column, ...columns];
  return [...columns.slice(0, index), column, ...columns.slice(index)];
};

/** Migrates saved OAPTable: legacy `id` ("ID No") → applicantId. Office Accounting ID stays optional. */
export function normalizeVisibleOAPColumns(columns: OfficeAccountingColumnConfig[]): OfficeAccountingColumnConfig[] {
  const knownIds = new Set(officeAccountingAllFieldsList.map((col) => col.id));
  const hadLegacyIdColumn = columns.some((col) => col.id === "id");

  let next: OfficeAccountingColumnConfig[] = columns.flatMap((col) => {
    if (col.id === "id") {
      const applicantIdCol = findOAPColumn("applicantId");
      if (!applicantIdCol) return [];
      return [{ ...applicantIdCol, width: resolveSavedOAPColumnWidth(col.width, applicantIdCol.width) }];
    }

    const canonical = findOAPColumn(col.id);
    if (!canonical) return [];
    return [{ ...canonical, width: resolveSavedOAPColumnWidth(col.width, canonical.width) }];
  });

  next = dedupeOAPColumns(next.filter((col) => knownIds.has(col.id)));

  // Forward migration: keep Certification & Equalization after Tuition Fee when Tuition is visible.
  if (next.some((col) => col.id === "tuitionFee") && !next.some((col) => col.id === "certificationEqualization")) {
    const certificationCol = findOAPColumn("certificationEqualization");
    if (certificationCol) next = insertOAPColumnAfter(next, { ...certificationCol }, "tuitionFee");
  }

  // Forward migration: Student Status after Date of Birth.
  if (next.some((col) => col.id === "dateOfBirth") && !next.some((col) => col.id === "studentStatus")) {
    const studentStatusCol = findOAPColumn("studentStatus");
    if (studentStatusCol) next = insertOAPColumnAfter(next, { ...studentStatusCol }, "dateOfBirth");
  }

  if (!hadLegacyIdColumn) return next;

  if (!next.some((col) => col.id === "applicantId")) {
    const applicantIdCol = findOAPColumn("applicantId");
    if (applicantIdCol) {
      next = insertOAPColumnBefore(next, applicantIdCol, "fullName");
    }
  }

  const courseIndex = next.findIndex((col) => col.id === "course");
  let degreeIndex = next.findIndex((col) => col.id === "levelEducation");
  if (courseIndex !== -1 && degreeIndex === -1) {
    const degreeColumn = findOAPColumn("levelEducation");
    if (degreeColumn) {
      next = insertOAPColumnBefore(next, { ...degreeColumn }, "course");
      degreeIndex = next.findIndex((col) => col.id === "levelEducation");
    }
  }

  const updatedCourseIndex = next.findIndex((col) => col.id === "course");
  const updatedDegreeIndex = next.findIndex((col) => col.id === "levelEducation");
  if (updatedCourseIndex !== -1 && updatedDegreeIndex !== -1 && updatedDegreeIndex > updatedCourseIndex) {
    const [degreeColumn] = next.splice(updatedDegreeIndex, 1);
    const courseIdxAfterRemoval = next.findIndex((col) => col.id === "course");
    next.splice(courseIdxAfterRemoval, 0, degreeColumn);
  }

  return next;
}

export const OFFICE_ACCOUNTING_GENERAL_STATUS_NON_SORTED = "Non Sorted";

export const officeAccountingGeneralStatusConst = [
  { id: "active", label: "Active" },
  { id: "on_leave", label: "On leave" },
  { id: "graduated", label: "Graduated" },
  { id: "non_sorted", label: OFFICE_ACCOUNTING_GENERAL_STATUS_NON_SORTED },
] as const;

/** Status Admission Payment options (Office Accounting pop-up). */
export const officeAccountingAdmissionPaymentStatusConst = [
  { id: "waiting", label: "Waiting" },
  { id: "debt", label: "Debt" },
  { id: "paid", label: "Paid" },
] as const;

/** Shared payment statuses: Status Invitation Fee, Balance Status USD, Status Missed Classes, Status Nostrification. */
export const officeAccountingPaymentFeeStatusConst = [
  { id: "paid", label: "Paid" },
  { id: "partially_paid", label: "Partially paid" },
  { id: "waiting_for_payment", label: "Waiting for payment" },
] as const;

export const OFFICE_ACCOUNTING_PAYMENT_FEE_STATUS_COLUMNS = new Set([
  "statusInvitationFee",
  "balanceStatusUsd",
  "statusMissedClasses",
  "statusNostrification",
]);

export const resolveOfficeAccountingPaymentFeeStatusLabel = (value: string | null | undefined): string => {
  const trimmed = value?.trim() ?? "";
  if (!trimmed) return "—";
  const match = officeAccountingPaymentFeeStatusConst.find(
    (status) => status.label.toLowerCase() === trimmed.toLowerCase() || status.id === trimmed.toLowerCase(),
  );
  return match?.label ?? trimmed;
};

/**
 * Status Invitation Fee from paid vs due amounts:
 * - paid 0 / null / undefined → Waiting for payment
 * - 0 < paid < invitationFee → Partially paid
 * - paid >= invitationFee → Paid
 */
export const resolveStatusInvitationFeeFromAmounts = (
  invitationFeePaid: string | number | null | undefined,
  invitationFee: string | number | null | undefined,
): string => {
  const paidRaw =
    invitationFeePaid == null
      ? ""
      : typeof invitationFeePaid === "number"
        ? String(invitationFeePaid)
        : invitationFeePaid.trim();
  if (!paidRaw) {
    return "Waiting for payment";
  }

  const paid = parseOfficeAccountingAmount(paidRaw);
  if (!Number.isFinite(paid) || paid <= 0) {
    return "Waiting for payment";
  }

  const fee = parseOfficeAccountingAmount(
    invitationFee == null ? "" : typeof invitationFee === "number" ? String(invitationFee) : invitationFee,
  );
  if (paid < fee) {
    return "Partially paid";
  }
  return "Paid";
};

/**
 * Balance Status USD / Status Missed Classes from due vs paid:
 * - due = 0 → Waiting for payment
 * - paid = 0 / null / undefined → Waiting for payment
 * - paid < due → Partially paid
 * - paid >= due → Paid
 */
export const resolveBalanceStatusUsdFromAmounts = (
  totalAmountUsd: string | number | null | undefined,
  totalPaidUsd: string | number | null | undefined,
): string => {
  const amount = parseOfficeAccountingAmount(
    totalAmountUsd == null ? "" : typeof totalAmountUsd === "number" ? String(totalAmountUsd) : totalAmountUsd,
  );
  if (!Number.isFinite(amount) || amount <= 0) {
    return "Waiting for payment";
  }

  const paidRaw =
    totalPaidUsd == null ? "" : typeof totalPaidUsd === "number" ? String(totalPaidUsd) : String(totalPaidUsd).trim();
  if (!paidRaw) {
    return "Waiting for payment";
  }

  const paid = parseOfficeAccountingAmount(paidRaw);
  if (!Number.isFinite(paid) || paid <= 0) {
    return "Waiting for payment";
  }

  if (paid < amount) {
    return "Partially paid";
  }
  return "Paid";
};

/** Alias for Status Missed Classes (Total Missed Classes EUR vs Payment Fact EUR Missed Classes). */
export const resolveStatusMissedClassesFromAmounts = resolveBalanceStatusUsdFromAmounts;

/** Alias for Status Nostrification (Total Nostrifications EUR vs Payment Fact EUR Notification). */
export const resolveStatusNostrificationFromAmounts = resolveBalanceStatusUsdFromAmounts;

export const resolveOfficeAccountingPaymentFeeStatusId = (value: string | null | undefined): string => {
  const trimmed = value?.trim() ?? "";
  if (!trimmed) return "empty";
  const match = officeAccountingPaymentFeeStatusConst.find(
    (status) => status.label.toLowerCase() === trimmed.toLowerCase() || status.id === trimmed.toLowerCase(),
  );
  return match?.id ?? "empty";
};

export const resolveOfficeAccountingAdmissionPaymentStatusLabel = (value: string | null | undefined): string => {
  const trimmed = value?.trim() ?? "";
  if (!trimmed) return "—";
  const match = officeAccountingAdmissionPaymentStatusConst.find(
    (status) => status.label.toLowerCase() === trimmed.toLowerCase() || status.id === trimmed.toLowerCase(),
  );
  return match?.label ?? trimmed;
};

export const resolveOfficeAccountingAdmissionPaymentStatusId = (value: string | null | undefined): string => {
  const trimmed = value?.trim() ?? "";
  if (!trimmed) return "empty";
  const match = officeAccountingAdmissionPaymentStatusConst.find(
    (status) => status.label.toLowerCase() === trimmed.toLowerCase() || status.id === trimmed.toLowerCase(),
  );
  return match?.id ?? "empty";
};

export const resolveOfficeAccountingGeneralStatusLabel = (value: string | null | undefined): string => {
  const trimmed = value?.trim() ?? "";
  if (!trimmed) return OFFICE_ACCOUNTING_GENERAL_STATUS_NON_SORTED;
  const match = officeAccountingGeneralStatusConst.find(
    (status) => status.label.toLowerCase() === trimmed.toLowerCase() || status.id === trimmed.toLowerCase(),
  );
  return match?.label ?? trimmed;
};

export const resolveOfficeAccountingGeneralStatusId = (value: string | null | undefined): string => {
  const trimmed = value?.trim() ?? "";
  if (!trimmed) return "non_sorted";
  const match = officeAccountingGeneralStatusConst.find(
    (status) => status.label.toLowerCase() === trimmed.toLowerCase() || status.id === trimmed.toLowerCase(),
  );
  return match?.id ?? "non_sorted";
};

export const formatOfficeAccountingGeneralStatusForApi = (value: string): string => {
  const trimmed = value.trim();
  if (!trimmed || trimmed === OFFICE_ACCOUNTING_GENERAL_STATUS_NON_SORTED) return "";
  return resolveOfficeAccountingGeneralStatusLabel(trimmed);
};

export const officeAccountingFilterStatusesConst = ["All", "Active", "On leave", "Graduated"];

export const officeAccountingHeaderSelectConst = [
  { id: "ALL", title: "All" },
  { id: "MALTA", title: "Malta" },
  { id: "POLAND", title: "Poland" },
  { id: "UKRAINE", title: "Ukraine" },
];

/** Campus filter / display options (`office` query: MALTA | POLAND | UKRAINE). */
export const officeAccountingCampusConst = [
  { id: "malta", label: "MALTA", value: "MALTA" },
  { id: "poland", label: "POLAND", value: "POLAND" },
  { id: "ukraine", label: "UKRAINE", value: "UKRAINE" },
] as const;

export const officeAccountingCampusFilterValues = officeAccountingCampusConst.map((item) => item.label);

export const resolveOfficeAccountingCampusId = (value?: string | null): string => {
  const trimmed = String(value ?? "").trim();
  if (!trimmed) return "empty";
  const matched = officeAccountingCampusConst.find(
    (item) =>
      item.id === trimmed.toLowerCase() ||
      item.label.toLowerCase() === trimmed.toLowerCase() ||
      item.value.toLowerCase() === trimmed.toLowerCase(),
  );
  return matched?.id ?? "empty";
};

export const resolveOfficeAccountingCampusLabel = (value?: string | null): string => {
  const trimmed = String(value ?? "").trim();
  if (!trimmed) return "—";
  const matched = officeAccountingCampusConst.find(
    (item) =>
      item.id === trimmed.toLowerCase() ||
      item.label.toLowerCase() === trimmed.toLowerCase() ||
      item.value.toLowerCase() === trimmed.toLowerCase(),
  );
  return matched?.label ?? trimmed.toUpperCase();
};
